'use client'

import React, {
  ChangeEvent,
  ClipboardEvent,
  ComponentProps,
  MouseEvent,
  useEffect,
  useState,
} from 'react'

import { getGraphemeLength, sliceByGrapheme, Slot } from '@/shared/lib'

import { Input } from './input'
import { Icon } from '../icon'

type Props = Omit<ComponentProps<typeof Input>, 'onReset' | 'maxLength'> & {
  editable?: boolean
  onReset?: () => void
  maxLength: number
}

export const GraphemeTextField = ({
  value: valueProp,
  onChange: onChangeProp,
  editable: editableProp,
  onReset,
  children,
  maxLength,
  ...props
}: Props) => {
  const [value, setValue] = useState(valueProp ?? '')

  const editable = editableProp ?? !(props.readOnly || props.disabled)

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    // 입력값이 maxLength를 넘는 경우 커서가 맨 끝으로 이동하는 것을 막기 위해 위치 조정
    if (getGraphemeLength(e.target.value) > maxLength) {
      const input = e.currentTarget
      const rawValue = input.value
      const selectionStart = input.selectionStart ?? 0

      const slicedValue = sliceByGrapheme(rawValue, maxLength)

      requestAnimationFrame(() => {
        const newSelectionStart =
          selectionStart -
          (getGraphemeLength(rawValue) - getGraphemeLength(slicedValue))
        input.setSelectionRange(newSelectionStart, newSelectionStart)
      })

      return
    }

    const slicedValue = sliceByGrapheme(e.target.value, maxLength)

    setValue(slicedValue)
    onChangeProp?.(e)
  }

  const handlePaste = (e: ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault()

    const input = e.currentTarget
    const pastedText = e.clipboardData.getData('text')

    const selectionStart = input.selectionStart ?? 0
    const selectionEnd = input.selectionEnd ?? 0

    // 선택 영역 앞/뒤 텍스트
    const beforeSelection = input.value.slice(0, selectionStart)
    const afterSelection = input.value.slice(selectionEnd)

    // 선택 제외한 현재 길이
    const currentLength =
      getGraphemeLength(beforeSelection) + getGraphemeLength(afterSelection)

    // 남은 공간
    const availableSpace = maxLength - currentLength

    if (availableSpace <= 0) {
      return
    }

    // 남은 공간만큼만 자르기
    const textToInsert = sliceByGrapheme(pastedText, availableSpace)

    // 커서 위치에 삽입해서 새 값 조합
    const newValue = beforeSelection + textToInsert + afterSelection

    setValue(newValue)
    onChangeProp?.({
      target: {
        value: newValue,
      },
    } as ChangeEvent<HTMLInputElement>)

    requestAnimationFrame(() => {
      const newSelectionStart = selectionStart + textToInsert.length
      input.setSelectionRange(newSelectionStart, newSelectionStart)
    })
  }

  const handleResetButtonClick = (event: MouseEvent) => {
    event.stopPropagation()

    setValue('')
    onChangeProp?.({
      target: {
        value: '',
      },
    } as ChangeEvent<HTMLInputElement>)
    onReset?.()
  }

  useEffect(() => {
    if (valueProp !== value) {
      setValue(valueProp ?? '')
    }
  }, [valueProp])

  return (
    <Input
      value={value}
      onChange={handleChange}
      onPaste={handlePaste}
      {...props}
    >
      {children}
      {editable && value && (
        <Slot name="right">
          <div className="flex gap-3">
            <button
              type="button"
              className="cursor-pointer"
              aria-label="텍스트 삭제"
              onClick={handleResetButtonClick}
              tabIndex={-1}
            >
              <Icon
                name="delete"
                size="24"
                strokeWidth={1.5}
                className="text-gray-300"
              />
            </button>
          </div>
        </Slot>
      )}
    </Input>
  )
}
